export interface Sqlite3MtLaneRecord {
    readonly version: 1
    readonly blobId: string
    readonly path: string
    readonly headBlobId: string
    readonly headLane: string
    readonly knownAt: number
    readonly lastOpenedAt: number
    readonly lastTouchedAt: number
    readonly dirty: boolean
    readonly dirtySince?: number
    readonly lastCleanAt?: number
    readonly lastSyncAt?: number
}

interface Sqlite3MtLaneIndexEnvelope {
    readonly version: 1
    readonly lanes: Record<string, Sqlite3MtLaneRecord>
}

export interface Sqlite3MtLaneIndexStorage {
    getItem(key: string): string | null
    setItem(key: string, value: string): void
    removeItem?(key: string): void
}

export interface Sqlite3MtLaneIdentity {
    readonly blobId: string
    readonly path: string
    readonly headBlobId: string
    readonly headLane: string
}

const SQLITE3MT_LANE_INDEX_PREFIX = 'sb:sqlite3mt-lanes:v1:'
const MEMORY_STORAGE = new Map<string, string>()

const MEMORY_STORAGE_ADAPTER: Sqlite3MtLaneIndexStorage = {
    getItem(key) {
        return MEMORY_STORAGE.get(key) ?? null
    },
    setItem(key, value) {
        MEMORY_STORAGE.set(key, value)
    },
    removeItem(key) {
        MEMORY_STORAGE.delete(key)
    },
}

function defaultStorage(): Sqlite3MtLaneIndexStorage {
    try {
        const storage = globalThis.localStorage
        if (storage != null) {
            return storage
        }
    } catch {
        // Browsers can deny localStorage; keep an in-memory index for this session.
    }
    return MEMORY_STORAGE_ADAPTER
}

function storageKey(blobId: string): string {
    return `${SQLITE3MT_LANE_INDEX_PREFIX}${blobId}`
}

function emptyEnvelope(): Sqlite3MtLaneIndexEnvelope {
    return { version: 1, lanes: {} }
}

function isValidRecord(blobId: string, value: unknown): value is Sqlite3MtLaneRecord {
    if (value == null || typeof value !== 'object') return false
    const record = value as Partial<Sqlite3MtLaneRecord>
    return record.version === 1
        && record.blobId === blobId
        && typeof record.path === 'string'
        && typeof record.headBlobId === 'string'
        && typeof record.headLane === 'string'
        && typeof record.knownAt === 'number'
        && typeof record.lastOpenedAt === 'number'
        && typeof record.lastTouchedAt === 'number'
        && typeof record.dirty === 'boolean'
}

function parseEnvelope(blobId: string, raw: string | null): Sqlite3MtLaneIndexEnvelope {
    if (raw == null) return emptyEnvelope()
    try {
        const parsed = JSON.parse(raw) as Partial<Sqlite3MtLaneIndexEnvelope>
        if (parsed.version !== 1 || parsed.lanes == null || typeof parsed.lanes !== 'object') {
            return emptyEnvelope()
        }

        const lanes: Record<string, Sqlite3MtLaneRecord> = {}
        for (const [path, record] of Object.entries(parsed.lanes)) {
            if (isValidRecord(blobId, record) && record.path === path) {
                lanes[path] = record
            }
        }
        return { version: 1, lanes }
    } catch {
        return emptyEnvelope()
    }
}

function compareLaneRecords(a: Sqlite3MtLaneRecord, b: Sqlite3MtLaneRecord): number {
    if (a.dirty !== b.dirty) return a.dirty ? -1 : 1
    if (a.lastTouchedAt !== b.lastTouchedAt) return b.lastTouchedAt - a.lastTouchedAt
    return a.path < b.path ? -1 : a.path > b.path ? 1 : 0
}

export class Sqlite3MtLaneIndex {
    private readonly key: string

    constructor(
        private readonly blobId: string,
        private readonly storage: Sqlite3MtLaneIndexStorage = defaultStorage(),
    ) {
        this.key = storageKey(blobId)
    }

    list(): Sqlite3MtLaneRecord[] {
        return Object.values(this.read().lanes).sort(compareLaneRecords)
    }

    listDirty(): Sqlite3MtLaneRecord[] {
        return this.list().filter(record => record.dirty)
    }

    markKnown(identity: Sqlite3MtLaneIdentity, now = Date.now()): Sqlite3MtLaneRecord {
        return this.update(identity.path, now, current => ({
            version: 1,
            blobId: this.blobId,
            path: identity.path,
            headBlobId: identity.headBlobId,
            headLane: identity.headLane,
            knownAt: current?.knownAt ?? now,
            lastOpenedAt: now,
            lastTouchedAt: now,
            dirty: current?.dirty ?? false,
            ...(current?.dirtySince != null ? { dirtySince: current.dirtySince } : {}),
            ...(current?.lastCleanAt != null ? { lastCleanAt: current.lastCleanAt } : {}),
            ...(current?.lastSyncAt != null ? { lastSyncAt: current.lastSyncAt } : {}),
        }))
    }

    markDirty(path: string, now = Date.now()): Sqlite3MtLaneRecord | null {
        return this.updateExisting(path, now, current => ({
            ...current,
            dirty: true,
            dirtySince: current.dirtySince ?? now,
            lastTouchedAt: now,
        }))
    }

    markClean(path: string, now = Date.now()): Sqlite3MtLaneRecord | null {
        return this.updateExisting(path, now, current => {
            const { dirtySince: _dirtySince, ...rest } = current
            return {
                ...rest,
                dirty: false,
                lastCleanAt: now,
                lastSyncAt: now,
                lastTouchedAt: now,
            }
        })
    }

    clear(path: string): void {
        const envelope = this.read()
        if (envelope.lanes[path] == null) return
        const next = { ...envelope.lanes }
        delete next[path]
        this.write({ version: 1, lanes: next })
    }

    private updateExisting(
        path: string,
        _now: number,
        fn: (current: Sqlite3MtLaneRecord) => Sqlite3MtLaneRecord,
    ): Sqlite3MtLaneRecord | null {
        const envelope = this.read()
        const current = envelope.lanes[path]
        if (current == null) return null
        const nextRecord = fn(current)
        this.write({
            version: 1,
            lanes: {
                ...envelope.lanes,
                [path]: nextRecord,
            },
        })
        return nextRecord
    }

    private update(
        path: string,
        _now: number,
        fn: (current: Sqlite3MtLaneRecord | undefined) => Sqlite3MtLaneRecord,
    ): Sqlite3MtLaneRecord {
        const envelope = this.read()
        const nextRecord = fn(envelope.lanes[path])
        this.write({
            version: 1,
            lanes: {
                ...envelope.lanes,
                [path]: nextRecord,
            },
        })
        return nextRecord
    }

    private read(): Sqlite3MtLaneIndexEnvelope {
        try {
            return parseEnvelope(this.blobId, this.storage.getItem(this.key))
        } catch {
            return emptyEnvelope()
        }
    }

    private write(envelope: Sqlite3MtLaneIndexEnvelope): void {
        try {
            this.storage.setItem(this.key, JSON.stringify(envelope))
        } catch {
            // Storage failures should not block SQLite open/sync.
        }
    }
}

export function createSqlite3MtLaneIndex(
    blobId: string,
    storage?: Sqlite3MtLaneIndexStorage,
): Sqlite3MtLaneIndex {
    return new Sqlite3MtLaneIndex(blobId, storage)
}
